import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator, ComparisonOperator } from '@common/enums';
import { CalendarEventModel, CalendarGroupModel, CalendarItemModel, OdataObject } from '@common/models';
import { ODataServiceFactory } from '@common/odata';
import { CalendarTag, CalendarTagsMap } from '@common/services/smart-search/calendar';
import { FilterOptions } from '@common/types';
import { filterByWords } from '@common/utils';
import { containsWrapper, getFilterByContains } from '@common/utils/functions/search';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class CalendarOdataService {
  private readonly http = inject(HttpClient);
  private readonly odataFactory = inject(ODataServiceFactory);

  readonly #event = CalendarTagsMap.get(CalendarTag.Event);
  // readonly #description = CalendarTagsMap.get(CalendarTag.Descr);

  private odataUsersService = this.odataFactory.CreateService<any>('BackupUsers');
  private odataCalendarGroupService = this.odataFactory.CreateService<CalendarGroupModel>('CalendarGroups');
  private odataCalendarEventsService = this.odataFactory.CreateService<CalendarEventModel>('CalendarEvents');
  // private odataCalendarAttachmentsService this.= odataFactory.CreateService<string>('CalendarAttachments');
  private odataMethods = this.odataFactory.CreateService<any>('');

  public accessor userId: string;
  public accessor keys: string[];

  hasAccessToEvents(userId: string): Observable<any> {
    const url = this.odataUsersService.Get(userId).GetUrl() + '/Touch ';

    return this.http.get<OdataObject<string>>(url);
  }

  getGroups(): Observable<CalendarGroupModel[]> {
    return this.odataUsersService
      .ItemProperty<OdataObject<CalendarGroupModel[]>>(this.userId, 'CalendarGroups')
      .pipe(map((result) => result.value));
  }

  getItems(groupId: string): Observable<CalendarItemModel[]> {
    return this.odataCalendarGroupService
      .ItemProperty<OdataObject<CalendarItemModel[]>>(groupId, 'CalendarItems')
      .pipe(map((result) => result.value));
  }

  private getBulkCalendarEvents = (filter: string) =>
    this.odataFactory.CreateService(`/BackupUsers(${this.userId})/GetBulkCalendarEvents?$filter=${filter}`);

  getEvents(startDate: Date, endDate: Date, recurrenceRule = false): Observable<CalendarEventModel[]> {
    const dateFilter = `EndDate ${ComparisonOperator.Gt} ${startDate.toISOString()} ${BooleanOperator.And} StartDate ${ComparisonOperator.Lt} ${endDate.toISOString()}`;
    const filter = recurrenceRule ? `(RecurrenceRule ${ComparisonOperator.Ne} null ${BooleanOperator.Or} (${dateFilter}))` : dateFilter;

    return this.http
      .post<OdataObject<CalendarEventModel[]>>(this.getBulkCalendarEvents(filter).Query().GetUrl(), { ItemIds: this.keys })
      .pipe(map((res) => res.value));
  }

  getEventRevisions(eventId: string): Observable<CalendarEventModel[]> {
    return this.odataCalendarEventsService
      .ItemProperty<OdataObject<CalendarEventModel[]>>(eventId, 'Revisions')
      .pipe(map((result) => result.value));
  }

  searchEvents(filter: string): Observable<CalendarEventModel[]> {
    return this.http
      .post<OdataObject<CalendarEventModel[]>>(this.getBulkCalendarEvents(filter).Query().GetUrl(), { ItemIds: this.keys })
      .pipe(map((res) => res.value));
  }

  updateFilter(searchObj: SmartSearchModel): string {
    const filter: string[] = [];

    // if (searchObj[this.#description.tag]) {
    //   const options: FilterOptions = {
    //     model: searchObj[this.#description.tag] as SmartSearchModelField[],
    //     prop: this.#description.prop
    //   };
    //
    //   filter.push(getFilterByContains(options));
    // }

    if (searchObj[this.#event.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#event.tag] as SmartSearchModelField[],
        prop: this.#event.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      // const keys = [this.#event.prop, this.#description.prop];
      const keys = [this.#event.prop];
      const values = keys.map((k) => containsWrapper(k, term));

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    return filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }

  restoreAll(): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreAllCalendars', { UserId: this.userId });
  }

  restoreGroup(Ids: string[]): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreCalendarGroups', { Ids });
  }

  restoreEvent(eventId: string): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreCalendarEvent', { EventId: eventId });
  }

  restoreCalendar(Ids: string[]): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreCalendars', { Ids });
  }

  deleteEvent(eventId: string, password: string): Observable<any> {
    return this.odataMethods.CustomCollectionAction('DeleteCalendarEvents', { Ids: [eventId], Password: password, WithAllRevisions: true });
  }

  deleteCalendar(calendarIds: string[], password: string): Observable<any> {
    return this.odataMethods.CustomCollectionAction('DeleteCalendarItems', { Ids: calendarIds, Password: password });
  }

  deleteGroup(groupIds: string[], password: string): Observable<any> {
    return this.odataMethods.CustomCollectionAction('DeleteCalendarGroups', { Ids: groupIds, Password: password });
  }
}
